import { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

const MARGIN = 4;

export interface SingleSelectOption {
  value: string;
  label: string;
  /** Optional second line (e.g. protocol number, code) used by the search and shown muted */
  sublabel?: string;
}

/**
 * Searchable single-select dropdown. Mirrors MultiSelectSearch styling but
 * exposes a simpler API for picking exactly one option (e.g. a study).
 *
 * - `value` is the currently selected option's `value`, or null.
 * - Typing filters the open menu by case-insensitive substring on label + sublabel.
 * - Click-outside closes the menu.
 *
 * The menu renders via a portal anchored to `document.body`, positioned with
 * `fixed` coordinates derived from the trigger's bounding rect — same fix as
 * ActionMenu.tsx. Without it, opening this inside any ancestor with
 * `overflow: auto/hidden` (a modal card, a scrollable panel) either clips
 * the menu or gets absorbed into that ancestor's own scrollable height,
 * forcing the whole container to scroll just to see the options (bug found
 * live: the "Copy design to sites" modal became unusably tall the moment
 * its site dropdown opened).
 */
export function SingleSelectSearch({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  id,
  className,
  /** Extra classes on the visible trigger box itself (e.g. an amber
   *  required-and-empty border to match a sibling group of plain inputs) —
   *  `className` only reaches the outer positioning wrapper, which can't
   *  affect the trigger's own look. */
  triggerClassName,
  disabled,
}: {
  options: SingleSelectOption[];
  value: string | null;
  onChange: (next: string | null) => void;
  placeholder?: string;
  id?: string;
  className?: string;
  triggerClassName?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [coords, setCoords] = useState<{ top: number; left: number; width: number; placeAbove: boolean } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Recompute position whenever we open or the window resizes/scrolls.
  useLayoutEffect(() => {
    if (!open || !rootRef.current) return;
    function place() {
      const rect = rootRef.current!.getBoundingClientRect();
      const menuHeight = menuRef.current?.offsetHeight ?? 280; // estimate before mount
      const spaceBelow = window.innerHeight - rect.bottom;
      const placeAbove = spaceBelow < menuHeight + MARGIN && rect.top > menuHeight + MARGIN;
      const top = placeAbove ? rect.top - menuHeight - MARGIN : rect.bottom + MARGIN;
      setCoords({ top, left: rect.left, width: rect.width, placeAbove });
    }
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (
        rootRef.current?.contains(e.target as Node) ||
        menuRef.current?.contains(e.target as Node)
      ) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.sublabel ?? '').toLowerCase().includes(q),
    );
  }, [options, query]);

  function pick(v: string) {
    onChange(v);
    setOpen(false);
    setQuery('');
  }

  function clear(e: React.MouseEvent) {
    e.stopPropagation();
    onChange(null);
    setQuery('');
  }

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <div
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        className={cn(
          'input flex items-center gap-1 min-h-[36px] pr-8',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          !selected && 'text-ink-muted',
          triggerClassName,
        )}
      >
        {selected ? (
          <span className="min-w-0 flex-1 truncate text-sm text-ink-primary">
            {selected.sublabel ? `${selected.sublabel} — ${selected.label}` : selected.label}
          </span>
        ) : (
          <span className="text-sm">{placeholder}</span>
        )}
        {selected && !disabled && (
          <button
            type="button"
            onClick={clear}
            className="absolute right-7 top-1/2 -translate-y-1/2 text-ink-muted hover:text-danger-700"
            aria-label="Clear selection"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
      </div>

      {open && !disabled && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: coords?.top ?? -9999,
            left: coords?.left ?? -9999,
            width: coords?.width,
          }}
          className={cn(
            'z-[200] rounded border border-surface-border bg-white shadow-card-3 overflow-hidden',
            !coords && 'invisible',
          )}
        >
          <div className="flex items-center gap-2 border-b border-surface-border px-2 py-1.5">
            <Search className="h-3.5 w-3.5 text-ink-muted" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="flex-1 bg-transparent text-xs outline-none"
            />
          </div>
          <ul className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-xs text-ink-muted">No matches.</li>
            )}
            {filtered.map((o) => {
              const checked = o.value === value;
              return (
                <li key={o.value}>
                  <button
                    type="button"
                    onClick={() => pick(o.value)}
                    className={cn(
                      'flex w-full items-start gap-2 px-3 py-1.5 text-left text-xs hover:bg-surface-muted',
                      checked && 'bg-navy-50',
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-ink-primary">{o.label}</span>
                      {o.sublabel && (
                        <span className="block truncate text-[10px] text-ink-muted">{o.sublabel}</span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>,
        document.body,
      )}
    </div>
  );
}
